import type { Scan } from '../api.ts';
import { asMoment } from './format.ts';

/**
 * A run that failed is an operational problem — a throttled read, a consent
 * withdrawn mid-collection — and never a control verdict, so none of these is
 * red. Amber carries "needs attention", and the shape of the marker changes
 * with the colour so the column survives greyscale.
 *
 * `interrupted` shares the amber marker with `failed` because it needs the same
 * glance — this run produced no evidence — while the label beside it draws the
 * distinction the colour cannot: our worker was restarted, the client's tenant
 * did nothing wrong, and nothing needs doing about it.
 */
const RUN_STATE: Record<string, string> = {
  complete: 'run-complete',
  failed: 'run-failed',
  interrupted: 'run-failed run-phrase',
  running: 'run-active',
  queued: 'run-active',
};

/**
 * Where the status word alone would mislead.
 *
 * Only where it earns its place: `complete`, `failed` and the pipeline stages
 * read perfectly well as themselves — the badge title-cases them — and renaming
 * them would be churn. Anything with no entry falls through to the raw status,
 * which is what this column has always shown and remains the safest answer for a
 * status the server has grown and this console has not been taught.
 *
 * `interrupted` earns one because the word on its own invites the reading the
 * status exists to prevent: that the client's tenant was at fault. Entries here
 * carry their own capitalisation and so are paired with `run-phrase`.
 */
const RUN_LABEL: Record<string, string> = {
  interrupted: 'Interrupted — not a failure',
};

interface AssessmentsProps {
  scans: Scan[];
  onOpenReport: (scanId: string, print: boolean) => void;
}

export const Assessments = ({ scans, onOpenReport }: AssessmentsProps) => {
  if (scans.length === 0) {
    return (
      <div className="empty">
        <span className="empty-title">No assessments yet</span>
        <p className="empty-body">
          Every completed assessment is kept, so this becomes the evidence trail an auditor reads.
          Start the first one from the Overview tab.
        </p>
      </div>
    );
  }

  return (
    <>
      <p className="note td-lead">
        Each completed assessment keeps its own stored report — a record of the configuration on the
        day it ran, never a live view. “Save as PDF” opens that report and your browser’s print
        dialogue; choose “Save as PDF” as the destination to produce the file.
      </p>
      <div className="tbl-wrap">
        <table className="tbl td-runs">
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">Completed</th>
              <th scope="col">State</th>
              <th scope="col">
                <span className="sr-only">Report</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {scans.map((scan) => (
              <tr key={scan.id}>
                <td className="num">{asMoment(scan.startedAt)}</td>
                <td className="num">{scan.completedAt ? asMoment(scan.completedAt) : '—'}</td>
                <td>
                  <span className={`td-run-state ${RUN_STATE[scan.status] ?? 'run-active'}`}>
                    {RUN_LABEL[scan.status] ?? scan.status}
                  </span>
                  {scan.error !== null && <p className="note td-run-error mono">{scan.error}</p>}
                </td>
                <td>
                  {scan.status === 'complete' && (
                    <div className="toolbar">
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => onOpenReport(scan.id, false)}
                      >
                        Open report
                      </button>
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => onOpenReport(scan.id, true)}
                      >
                        Save as PDF
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
